<script setup lang="ts">
  import { computed, type PropType, useId } from 'vue';
  import { useFormContext, getModelFromContext, setModelWithContext } from '@othercommerce/foundation';

  type RadioOption = { label: string, value: any };

  defineOptions({
    inheritAttrs: false,
  });

  const context = useFormContext();
  const random = useId();

  const props = defineProps({
    id: { type: String, required: false },
    name: { type: String, required: false },
    label: { type: String, required: false },
    required: { type: Boolean, required: false, default: false },
    readonly: { type: Boolean, required: false, default: false },
    inline: { type: Boolean, required: false, default: false },
    options: { type: Array as PropType<RadioOption[]>, required: false, default: [] },
  });

  const model = defineModel({
    get: (value) => getModelFromContext(props.name, context, value),
    set: (value) => setModelWithContext(props.name, context, value),
  });

  const readonly = computed(() => {
    return Boolean(props.readonly || (context && context.readonly.value));
  });

  const id = computed(() => {
    return props.id ?? random;
  });
</script>
<template>
  <FormController v-bind="{ id, name, label, required, inline }">
    <div class="radio-tabs-group" :class="{'radio-tabs-group--more-tabs' : options.length > 2}">
      <label v-for="option in options" class="radio-tab">
        <input type="radio" v-bind="{ name, readonly }" :value="option.value" v-model="model" />
        <span class="radio-tab-label">{{ option.label }}</span>
      </label>
    </div>
  </FormController>
</template>
<style scoped lang="scss">
  .radio-tabs-group {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(50px, 1fr));
    box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
    background: var(--sf-input-background-disabled);
    border: none;
    line-height: var(--sf-input-line-height);
    border-radius: var(--sf-input-border-radius);
    padding: var(--sf-input-error-padding-y);
    width: 100%;

    .radio-tab {
      display: contents;

      .radio-tab-label {
        font-weight: var(--sf-select-checked-font-weight);
        font-size: var(--sf-input-label-font-size);
        padding: var(--sf-input-padding-top) var(--sf-input-padding-left);
        border-radius: var(--sf-input-border-radius);
        text-align: center;
        cursor: pointer;
        color: var(--sf-radio-label-available-color);
        transition-property: var(--sf-input-transition-property);
        transition-timing-function: var(--sf-input-transition-timing-func);
        transition-duration: var(--sf-input-transition-duration);

        @media(hover: hover) {
          &:hover {
            color: var(--sf-radio-label-color);
          }
        }
      }

      input {
        display: none;

        &:checked + .radio-tab-label {
          color: var(--sf-radio-label-color);
          background: var(--sf-select-checked-background);
          box-shadow: 0 1px 3px 0 rgba(100, 116, 139, 0.1),
          0 1px 2px -1px rgba(100, 116, 139, 0.15);
        }
      }
    }
  }

  .input {
    background-color: var(--sf-input-background);
    border: var(--sf-input-border-bottom-width) var(--sf-input-border-style) var(--sf-input-border);
    border-radius: var(--sf-input-border-radius);
    box-shadow: var(--sf-input-box-shadow);
    color: var(--sf-input-color);
    cursor: text;
    display: flex;
    font-size: var(--sf-input-font-size);
    gap: 0.25rem;
    line-height: var(--sf-input-line-height);
    padding: var(--sf-input-padding-top) var(--sf-input-padding-right) var(--sf-input-padding-bottom) var(--sf-input-padding-left);
    width: 100%;

    &::placeholder {
      color: var(--input-placeholder-color);
    }

    &:read-only {
      color: var(--sf-input-color-disabled);
      background-color: var(--sf-input-background-disabled);
      border-color: var(--sf-input-border-disabled);
    }

    &:focus-visible {
      border-color: var(--sf-input-border-focus) !important;
      outline: 3px solid color-mix(in srgb, var(--sf-input-border-focus), transparent 75%) !important;
      outline-offset: 0;
    }

    &:disabled {
      color: var(--sf-input-color-disabled);
      background-color: var(--sf-input-background-disabled);
      border-color: var(--sf-input-border-disabled);
      cursor: not-allowed;
    }
  }

  @media screen and (max-width: 575px) {
    .radio-tabs-group--more-tabs {
      grid-template-columns: 1fr;
    }
  }
</style>
